import { useCallback, useEffect, useState } from 'react';
import type { Editor } from '@tiptap/react';
import { NodeSelection } from '@tiptap/pm/state';
import { SELECTION_LIST_PLACEHOLDER, TEXT_MARK_PLACEHOLDER } from './placeholderTypes';

interface PlaceholderPos {
  pos: number;
  isSelectionList: boolean;
}

function collectPlaceholders(editor: Editor): PlaceholderPos[] {
  const found: PlaceholderPos[] = [];
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === SELECTION_LIST_PLACEHOLDER) {
      if (!(node.attrs.skipped as boolean)) found.push({ pos, isSelectionList: true });
    } else if (node.type.name === TEXT_MARK_PLACEHOLDER && !(node.attrs.value as string)) {
      found.push({ pos, isSelectionList: false });
    }
    return true;
  });
  return found.sort((a, b) => a.pos - b.pos);
}

export function usePlaceholderNavigation({
  editor,
  onActivate,
}: {
  editor: Editor;
  onActivate?: (pos: number) => void;
}) {
  const [activeListPos, setActiveListPos] = useState<number | null>(null);
  const [isPlaceholderActive, setIsPlaceholderActive] = useState(false);

  const select = useCallback(
    (target: PlaceholderPos) => {
      if (!target.isSelectionList) onActivate?.(target.pos);
      editor.chain().focus().setNodeSelection(target.pos).run();
      setActiveListPos(target.isSelectionList ? target.pos : null);
    },
    [editor, onActivate]
  );

  const deselect = useCallback(() => {
    const sel = editor.state.selection;
    if (sel instanceof NodeSelection) {
      editor.chain().setTextSelection(sel.from).run();
    }
    setActiveListPos(null);
    setIsPlaceholderActive(false);
  }, [editor]);

  const goToNextFrom = useCallback(
    (fromPos: number) => {
      const all = collectPlaceholders(editor);
      const next = all.find((p) => p.pos >= fromPos);
      if (!next) return false;
      select(next);
      return true;
    },
    [editor, select]
  );

  const goToPrevFrom = useCallback(
    (fromPos: number) => {
      const all = collectPlaceholders(editor);
      const prev = [...all].reverse().find((p) => p.pos < fromPos);
      if (!prev) return false;
      select(prev);
      return true;
    },
    [editor, select]
  );

  // Open the popup only when a selection-list node is node-selected, not when a caret merely sits
  // next to it, so arrowing past a chip or Escape landing after one doesn't re-open it.
  useEffect(() => {
    const syncActiveListFromSelection = () => {
      const sel = editor.state.selection;
      const nodeName = sel instanceof NodeSelection ? sel.node.type.name : null;
      const onListNode = nodeName === SELECTION_LIST_PLACEHOLDER;
      setActiveListPos(onListNode ? sel.from : null);
      setIsPlaceholderActive(onListNode || nodeName === TEXT_MARK_PLACEHOLDER);
    };
    editor.on('selectionUpdate', syncActiveListFromSelection);
    editor.on('update', syncActiveListFromSelection);
    return () => {
      editor.off('selectionUpdate', syncActiveListFromSelection);
      editor.off('update', syncActiveListFromSelection);
    };
  }, [editor]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent): boolean => {
      if (event.key !== 'Tab') return false;
      const all = collectPlaceholders(editor);
      if (all.length === 0) return false;
      const caret = editor.state.selection.from;
      const forward = !event.shiftKey;
      const next = forward
        ? all.find((p) => p.pos > caret)
        : [...all].reverse().find((p) => p.pos < caret);
      if (!next) return false; // last/first: fall back to native Tab
      event.preventDefault();
      select(next);
      return true;
    },
    [editor, select]
  );

  return { goToNextFrom, goToPrevFrom, onKeyDown, activeListPos, isPlaceholderActive, deselect };
}
